<template>
  <div class="obras-lista">
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Lista de Obras</h1>
        <p class="content-header-subtitle">Gestiona todas las obras del sistema</p>
      </div>
      <div class="content-header-right">
        <b-button variant="primary" @click="nuevaObra">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          Nueva Obra
        </b-button>
      </div>
    </div>

    <!-- Filtros -->
    <b-card class="mb-3">
      <b-row>
        <b-col md="3">
          <b-form-group label="Buscar">
            <b-form-input 
              v-model="filtros.busqueda" 
              placeholder="Buscar por dirección, municipio..."
              @input="filtrarObras"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Estado">
            <b-form-select 
              v-model="filtros.estado" 
              :options="opcionesEstado"
              @change="filtrarObras"
            >
              <template #first>
                <b-form-select-option :value="null">Todos</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Tipo">
            <b-form-select 
              v-model="filtros.tipo" 
              :options="opcionesTipo"
              @change="filtrarObras"
            >
              <template #first>
                <b-form-select-option :value="null">Todos</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Fase">
            <b-form-select 
              v-model="filtros.fase" 
              :options="opcionesFase"
              @change="filtrarObras"
            >
              <template #first>
                <b-form-select-option :value="null">Todas</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-button variant="outline-secondary" @click="limpiarFiltros">
            <feather-icon icon="XIcon" size="14" class="mr-50" />
            Limpiar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <!-- Indicador de carga -->
      <div v-if="cargando" class="text-center py-4">
        <b-spinner variant="primary" label="Cargando obras..."></b-spinner>
        <p class="mt-2 text-muted">Cargando obras...</p>
      </div>
      
      <!-- Tabla de obras -->
      <b-table
        v-else
        :items="obrasFiltradas"
        :fields="obrasFields"
        responsive
        striped
        hover
        show-empty
        empty-text="No hay obras que coincidan con los filtros"
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(direccion)="data">
          <div class="text-truncate" style="max-width: 200px;" :title="data.item.direccion">
            {{ data.item.direccion }}
          </div>
        </template>
        
        <template #cell(municipio)="data">
          <div>
            <strong>{{ data.item.municipio }}</strong><br>
            <small class="text-muted">{{ data.item.provincia }}</small>
          </div>
        </template>
        
        <template #cell(tipoObra)="data">
          <b-badge variant="info">
            {{ data.item.nombreTipoObra || 'Sin tipo' }}
          </b-badge>
        </template>
        
        <template #cell(estadoObra)="data">
          <b-badge :variant="getEstadoVariant(data.item.nombreEstadoObra || '')">
            {{ data.item.nombreEstadoObra || 'Sin estado' }}
          </b-badge>
        </template>
        
        <template #cell(fase)="data">
          <b-badge variant="secondary">
            {{ data.item.nombreFase || 'Sin fase' }}
          </b-badge>
        </template>
        
        <template #cell(fechas)="data">
          <div class="text-center">
            <div><strong>Inicio:</strong> {{ formatearFecha(data.item.fechaInicioObra) }}</div>
            <div><strong>Fin:</strong> {{ formatearFecha(data.item.fechaFinObra) }}</div>
          </div>
        </template>
        
        <template #cell(empresa)="data">
          <div class="text-truncate" style="max-width: 150px;" :title="data.item.nombreEmpresa">
            {{ data.item.nombreEmpresa || 'Sin empresa' }}
          </div>
        </template>
        
        <template #cell(acciones)="data">
          <b-button-group size="sm">
            <b-button variant="outline-primary" @click="verObra(data.item.idObra)" v-b-tooltip.hover title="Ver detalles">
              <feather-icon icon="EyeIcon" size="14" />
            </b-button>
            <b-button variant="outline-warning" @click="editarObra(data.item.idObra)" v-b-tooltip.hover title="Editar">
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
            <b-button variant="outline-danger" @click="eliminarObra(data.item.idObra)" v-b-tooltip.hover title="Eliminar">
              <feather-icon icon="TrashIcon" size="14" />
            </b-button>
          </b-button-group>
        </template>
      </b-table>
      
      <!-- Paginación -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div class="text-muted">
          Mostrando {{ (currentPage - 1) * perPage + 1 }} a {{ Math.min(currentPage * perPage, obrasFiltradas.length) }} de {{ obrasFiltradas.length }} obras
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="obrasFiltradas.length"
          :per-page="perPage"
          size="sm"
          class="mb-0"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import { ObrasListaMixin } from '@/@core/mixins/catalogos'

export default {
  name: 'ObrasLista',
  mixins: [ObrasListaMixin],
  data() {
    return {
      obrasFields: [
        { key: 'direccion', label: 'Dirección', sortable: true },
        { key: 'municipio', label: 'Ubicación', sortable: true },
        { key: 'tipoObra', label: 'Tipo', sortable: true },
        { key: 'estadoObra', label: 'Estado', sortable: true },
        { key: 'fase', label: 'Fase', sortable: true },
        { key: 'fechas', label: 'Fechas', sortable: false },
        { key: 'empresa', label: 'Empresa', sortable: true },
        { key: 'acciones', label: 'Acciones', sortable: false },
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
.obras-lista {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

// Estilos para la tabla
.table {
  font-size: 0.9rem;
  
  th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
    vertical-align: middle;
  }
  
  td {
    vertical-align: middle;
    border-top: 1px solid #dee2e6;
  }
  
  .text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Estilos para badges
.badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
}

// Estilos para filtros
.form-group {
  margin-bottom: 0;
  
  .form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.25rem;
  }
  
  .form-control, .form-select {
    font-size: 0.875rem;
    border-radius: 0.375rem;
    border: 1px solid #ced4da;
    
    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }
}

// Estilos para botones
.btn {
  border-radius: 0.375rem;
  font-weight: 500;
  
  &.btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
}

// Estilos para paginación
.pagination {
  .page-link {
    border-radius: 0.375rem;
    margin: 0 0.125rem;
    border: 1px solid #dee2e6;
    color: #007bff;
    
    &:hover {
      background-color: #e9ecef;
      border-color: #adb5bd;
    }
  }
  
  .page-item.active .page-link {
    background-color: #007bff;
    border-color: #007bff;
  }
}

// Responsive
@media (max-width: 768px) {
  .obras-lista {
    padding: 1rem;
  }
  
  .content-header {
    flex-direction: column;
    align-items: stretch;
    
    .content-header-right {
      margin-top: 1rem;
    }
  }
  
  .content-header-title {
    font-size: 1.5rem;
  }
  
  .table-responsive {
    font-size: 0.8rem;
  }
  
  .btn-group .btn {
    padding: 0.125rem 0.25rem;
  }
}

// Estilos para tooltips
.tooltip {
  font-size: 0.75rem;
}

// Estilos para el contador de resultados
.text-muted {
  font-size: 0.875rem;
}
</style>
